import type {
  CookieConsentConfig,
  CookieConsentProductResult,
  CookieConsentDetailedEntry,
  CookiePolicyType,
} from '../types/cookieConsent';

const COOKIE_CONSENT_CONFIG: CookieConsentConfig = {
  policies: ['essential', 'analytics', 'functional'],
  parties: [
    {
      policy: 'analytics',
      vendors: [
        {
          name: 'Wootric',
          key: 'wootric',
          description: 'Net Promoter Score surveys to collect user feedback and improve our product',
          vendorUrl: 'https://www.wootric.com/privacy-policy/',
          partyType: 'Third Party',
        },
      ],
    },
  ],
  applicationId: '00c333f6-0888-4b6c-8572-4401e93e841c',
  policyVersion: '2025-01',
  policyUrl: 'https://www.theaccessgroup.com/en-gb/privacy-policy/',
};

/**
 * Check if a specific policy type has been consented to.
 *
 * Consent can be:
 * - `'*'` → user accepted all cookies
 * - `null` → user accepted essential only
 * - `CookieConsentDetailedEntry[]` → granular consent per policy
 */
export function hasPolicyConsent(
  consent: CookieConsentProductResult | null,
  policyType: CookiePolicyType,
): boolean {
  if (!consent) {
    return false;
  }

  // '*' means the user accepted all cookies
  if (consent.consent === '*') {
    return true;
  }

  // null means the user accepted essential only
  if (consent.consent === null) {
    return policyType === 'essential';
  }

  // Detailed consent: check if the policy type is in the array
  return (consent.consent as CookieConsentDetailedEntry[]).some(
    (entry) => entry.policy === policyType,
  );
}

/**
 * Detect the Evo environment from the current hostname.
 * The SDK uses this to resolve the correct Evo origin for secure messaging.
 *
 * Valid environment keys expected by the SDK:
 *   local, dev, preprod, staging, demo, prod, prod-anz, prod-us, prod-eu,
 *   integration-us, integration-eu
 */
export function detectEnvironment(): string {
  const hostname = window.location.hostname;

  if (hostname === 'localhost' || hostname === '127.0.0.1' || hostname === '0.0.0.0') {
    return 'local';
  }

  if (hostname.includes('preprod')) {
    return 'preprod';
  }

  if (hostname.includes('staging')) {
    return 'staging';
  }

  if (hostname.includes('demo')) {
    return 'demo';
  }

  if (hostname.includes('dev')) {
    return 'dev';
  }

  return 'prod';
}

/**
 * Initialise cookie consent using the Access Cookie Policies SDK.
 * Returns the consent result for this product, or null if the SDK is not available.
 */
export async function initialiseCookieConsent(): Promise<CookieConsentProductResult | null> {
  if (!window.AccessCookiePolicies?.SDK?.tabInitialise) {
    console.warn('Cookie Policies SDK not available');
    return null;
  }

  try {
    const environment = detectEnvironment();
    const consent = await window.AccessCookiePolicies.SDK.tabInitialise({
      config: COOKIE_CONSENT_CONFIG,
      environment,
    });
    return consent;
  } catch (error) {
    console.error('Failed to initialise cookie consent:', error);
    return null;
  }
}

export const cookieConsentConfig = COOKIE_CONSENT_CONFIG;
